import { supabase } from '@/integrations/supabase/client';
import { PUBLIC_PROFILE_COLUMNS, type PublicUserProfile } from '@/lib/profileColumns';
import type { Database, Json } from '@/integrations/supabase/types';

type UserProfile = PublicUserProfile;
type UserProfileUpdate = Database['public']['Tables']['user_profiles']['Update'];

export const profileService = {
  async getCurrentUserProfile(): Promise<UserProfile | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    // Ensure profile exists first
    await this.ensureUserProfile(user.id);

    const { data, error } = await supabase
      .from('user_profiles')
      .select(PUBLIC_PROFILE_COLUMNS)
      .eq('id', user.id)
      .single();

    if (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }

    return data as UserProfile;
  },

  async getUserProfile(userId: string): Promise<UserProfile | null> {
    const { data, error } = await supabase
      .from('user_profiles')
      .select(PUBLIC_PROFILE_COLUMNS)
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }

    return data as UserProfile;
  },

  async ensureUserProfile(userId: string): Promise<void> {
    const { data: existingProfile } = await supabase
      .from('user_profiles')
      .select('id')
      .eq('id', userId)
      .maybeSingle();

    if (!existingProfile) {
      await supabase
        .from('user_profiles')
        .insert({
          id: userId,
          username: `user_${userId.substring(0, 8)}`,
          full_name: 'User',
          total_xp: 0
        });
    }
  },

  async updateProfile(updates: UserProfileUpdate): Promise<UserProfile | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    // Ensure profile exists
    await this.ensureUserProfile(user.id);

    const { data, error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', user.id)
      .select(PUBLIC_PROFILE_COLUMNS)
      .single();

    if (error) {
      console.error('Error updating user profile:', error);
      return null;
    }

    return data as UserProfile;
  },

  async getUserStats(userId?: string) {
    const targetUserId = userId || (await supabase.auth.getUser()).data.user?.id;
    if (!targetUserId) return null;

    const { data, error } = await supabase
      .from('user_stats')
      .select('*')
      .eq('user_id', targetUserId)
      .order('date_recorded', { ascending: false });

    if (error) {
      console.error('Error fetching user stats:', error);
      return null;
    }

    return data;
  },

  async updateStreak(): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return;
    }
    const { error } = await supabase.rpc('update_user_streak', { p_user_id: user.id });

    if (error) {
      console.error('Error updating user streak:', error);
    }
  },

  async addXP(
    amount: number,
    activityType: string,
    activityData: Json = {},
    relatedContentType?: string,
    relatedContentId?: string
  ): Promise<boolean> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return false;
    }

    const { error } = await supabase.rpc('add_xp_and_log_activity', {
      p_user_id: user.id,
      p_amount: amount,
      p_activity_type: activityType,
      p_activity_data: activityData,
      p_related_content_type: relatedContentType,
      p_related_content_id: relatedContentId
    });

    if (error) {
      console.error(`Error adding XP for ${activityType}:`, error);
      return false;
    }

    return true;
  }
};
